/** Classify errors for observability — no dashboard coupling; GW / logs consume JSON as-is */

export type ClientErrorSourceBucket = 'first_party' | 'third_party' | 'unknown';

const THIRD_PARTY_SNIPPETS = [
  // Ads / analytics (extend as needed)
  'googletagmanager.com',
  'google-analytics.com',
  'googleadservices.com',
  'google.com/pagead',
  'doubleclick.net',
  'facebook.net',
  'connect.facebook.net',
  'analytics.tiktok.com',
  'tiktok.com',
  'ads-twitter.com',
  'twitter.com',
  'linkedin.com',
  'clarity.ms',
  'hotjar.com',
  'segment.com',
  'snap.licdn.com',
  'pinimg.com',
  'ct.pinterest.com',
  'bat.bing.com',
  'bat.bing.net',
  'adform.net',
  'revotas.com',
  'yango.com',
  'cdn.jsdelivr.net',
  'unpkg.com',
];

export function extractUrlsFromText(text: string): string[] {
  const out: string[] = [];
  const re = /https?:\/\/[^\s)'"`]+/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text))) {
    try {
      out.push(m[0].replace(/[,;.]+$/, ''));
    } catch {
      /* ignore */
    }
  }
  return out;
}

export function classifyClientErrorSource(input: {
  message: string;
  stack?: string;
  srcFile?: string;
  resourceUrl?: string;
  /** Browser current page hostname when available */
  pageHost?: string;
}): { bucket: ClientErrorSourceBucket; hints: string[] } {
  const hints = new Set<string>();
  const blob = [
    input.message,
    input.stack || '',
    input.srcFile || '',
    input.resourceUrl || '',
  ].join('\n');

  for (const url of extractUrlsFromText(blob)) {
    hints.add(url);
  }

  const envHost =
    typeof process !== 'undefined' && process.env.NEXT_PUBLIC_WEBSITE_URL
      ? (() => {
          try {
            return new URL(process.env.NEXT_PUBLIC_WEBSITE_URL).hostname;
          } catch {
            return '';
          }
        })()
      : '';

  const hostSelf = input.pageHost || envHost || 'hizlicicek.com';

  let thirdScore = 0;
  let firstScore = 0;

  const isFirstPartyHost = (h: string) =>
    h === hostSelf ||
    (!!envHost && h === envHost) ||
    h.endsWith('.hizlicicek.com') ||
    h === 'localhost';

  // If a resourceUrl exists and it's clearly third-party, treat it as third-party regardless of stack.
  if (input.resourceUrl) {
    try {
      const rh = new URL(input.resourceUrl).hostname;
      if (!isFirstPartyHost(rh)) {
        if (THIRD_PARTY_SNIPPETS.some((s) => input.resourceUrl!.includes(s))) {
          return { bucket: 'third_party', hints: [...hints].slice(0, 12) };
        }
      }
    } catch {
      /* ignore */
    }
  }

  for (const u of hints) {
    try {
      const h = new URL(u).hostname;
      if (isFirstPartyHost(h)) {
        firstScore += 2;
        continue;
      }
      if (u.includes('/_next/static/') || u.includes('/_next/')) {
        firstScore += 2;
        continue;
      }
      if (THIRD_PARTY_SNIPPETS.some((s) => u.includes(s))) {
        thirdScore += 3;
      }
    } catch {
      /* ignore */
    }
  }

  if (input.resourceUrl) {
    try {
      const rh = new URL(input.resourceUrl).hostname;
      if (isFirstPartyHost(rh)) firstScore += 3;
      else if (THIRD_PARTY_SNIPPETS.some((s) => input.resourceUrl!.includes(s))) thirdScore += 4;
      else thirdScore += 1;
    } catch {
      /* ignore */
    }
  }

  for (const frag of THIRD_PARTY_SNIPPETS) {
    if (blob.includes(frag)) thirdScore += 1;
  }

  if (/\/_next\/static\//i.test(blob) || blob.includes(hostSelf)) firstScore += 1;

  if (thirdScore >= 2 && thirdScore >= firstScore) {
    return { bucket: 'third_party', hints: [...hints].slice(0, 12) };
  }
  if (firstScore >= 1 && firstScore > thirdScore) {
    return { bucket: 'first_party', hints: [...hints].slice(0, 12) };
  }
  return { bucket: 'unknown', hints: [...hints].slice(0, 12) };
}
